import React, { useState, useMemo, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { events as eventsApi, eventList, celebrationList } from '../../api/endpoints';
import { getList } from '../../api/client';
import { formatCurrency, formatTime, formatDate } from '../../utils/format';
import useStyles from '../../theme/useStyles';
import {
  Drawer, Badge, Button, Modal, Input, Select, SearchableSelect, Notification
} from '../../components/ui';
import PrintButton from '../../components/ui/PrintButton';
import { useFetchList, useNotification } from '../../hooks';
import type { CSSProperties } from 'react';
import type { ChurchEvent } from '../../types';

const WEEKDAYS_SHORT = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const WEEKDAYS_LONG = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
const MAX_PILLS_PER_DAY = 3;

const CATEGORIES = ['Worship', 'Youth', 'Choir', 'Sunday School', 'Outreach', 'Committee', 'Fellowship', 'Special'];
const CATEGORY_COLORS: Record<string, string> = {
  Worship: '#6B4277', Youth: '#1976D2', Choir: '#00897B', 'Sunday School': '#F57C00',
  Outreach: '#43A047', Committee: '#5E35B1', Fellowship: '#D81B60', Special: '#C62828'
};
const BIRTHDAY_COLOR = '#F59E0B';
const ANNIVERSARY_COLOR = '#DB2777';

const toDateStr = (date: Date) =>
  `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;

const isSameDay = (a: Date, b: Date) =>
  a.getDate() === b.getDate() && a.getMonth() === b.getMonth() && a.getFullYear() === b.getFullYear();

const byTime = (a, b) => String(a.time || '~').localeCompare(String(b.time || '~'));

const kindOf = (ev): 'birthday' | 'anniversary' | 'cancelled' | 'event' => {
  if (ev.isLifeEvent) return String(ev.eventId).startsWith('BDAY') ? 'birthday' : 'anniversary';
  if (ev.eventStatus === 'Cancelled') return 'cancelled';
  return 'event';
};

/** Stable colour for any category not in the fixed palette. */
const hashColor = (s: string) => {
  let h = 0;
  for (let i = 0; i < s.length; i += 1) h = s.charCodeAt(i) + ((h << 5) - h);
  return `hsl(${Math.abs(h) % 360}, 45%, 45%)`;
};

const colorOf = (ev, fallback: string) => {
  const k = kindOf(ev);
  if (k === 'birthday') return BIRTHDAY_COLOR;
  if (k === 'anniversary') return ANNIVERSARY_COLOR;
  if (k === 'cancelled') return '#9CA3AF';
  return CATEGORY_COLORS[ev.category] || (ev.category ? hashColor(ev.category) : fallback);
};

const formatFriendlyDate = (dateStr) => {
  if (!dateStr) return '';
  const [y, m, d] = String(dateStr).split('-');
  if (!d) return dateStr;
  return new Date(+y, +m - 1, +d).toLocaleDateString('en-US', {
    weekday: 'long', month: 'long', day: 'numeric', year: 'numeric'
  });
};

// A birthday / anniversary row from /api/celebrations, mapped onto a calendar
// pill. The occurrence lands in the range's start year, unless the range wraps
// New Year and this date is on the January side.
const toLifeEvent = (row, fromMd: string, rangeFrom: string, rangeTo: string) => {
  const md = String(row.md || '');
  const eventYear = md >= fromMd ? Number(rangeFrom.slice(0, 4)) : Number(rangeTo.slice(0, 4));
  const isBday = row.type === 'BIRTHDAY';
  const kind = isBday ? 'bday' : 'anniv';
  return {
    id: `${kind}-${row.id}`,
    eventId: `${kind.toUpperCase()}-${row.id}`,
    eventName: `${isBday ? '🎂' : '💍'} ${row.name}'s ${isBday ? 'Birthday' : 'Anniversary'}`,
    eventDate: `${eventYear}-${md}`,
    time: 'All Day',
    venue: 'Church System',
    organiser: 'Auto Record',
    budget: 0,
    isLifeEvent: true,
    memberName: row.name
  };
};

const EMPTY_QUICK = { eventName: '', category: '', eventDate: '', time: '', venue: '' };

const ChurchCalendar = ({ isDarkMode }) => {
  const navigate = useNavigate();
  const styles = useStyles(isDarkMode);
  const t = styles.t;

  const { notification, notify, dismiss } = useNotification();

  const [currentDate, setCurrentDate] = useState(new Date());
  const [viewMode, setViewMode] = useState('month');
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [selectedDate, setSelectedDate] = useState(null);

  // Filters
  const [hideBirthdays, setHideBirthdays] = useState(false);
  const [hideAnniversaries, setHideAnniversaries] = useState(false);
  const [categoryFilter, setCategoryFilter] = useState('');

  // Quick-add
  const [quickAdd, setQuickAdd] = useState<null | { eventName: string; category: string; eventDate: string; time: string; venue: string }>(null);
  const [saving, setSaving] = useState(false);
  // What is already booked on the chosen date. Set by the first Save press,
  // which warns instead of writing; the next press goes ahead. Cleared whenever
  // the date or time changes so it can never describe a date since moved away
  // from.
  const [clash, setClash] = useState<ChurchEvent[]>([]);

  // The calendar loads only the visible window: a month shows its own days, a
  // week/day its own range, and the agenda a few months forward. Navigating
  // months fetches just that month — never every event or every member.
  const [rangeFrom, rangeTo] = useMemo<[string, string]>(() => {
    const d = currentDate;
    if (viewMode === 'week') {
      const s = new Date(d); s.setDate(s.getDate() - s.getDay());
      const e = new Date(s); e.setDate(s.getDate() + 6);
      return [toDateStr(s), toDateStr(e)];
    }
    if (viewMode === 'day') return [toDateStr(d), toDateStr(d)];
    if (viewMode === 'agenda') {
      const s = new Date(d.getFullYear(), d.getMonth(), 1);
      const e = new Date(s); e.setDate(e.getDate() + 92);
      return [toDateStr(s), toDateStr(e)];
    }
    const s = new Date(d.getFullYear(), d.getMonth(), 1);
    const e = new Date(d.getFullYear(), d.getMonth() + 1, 0);
    return [toDateStr(s), toDateStr(e)];
  }, [currentDate, viewMode]);

  /** The view already shows today — "Today" is lit rather than clickable-looking (CHM-017). */
  const viewingToday = useMemo(() => {
    const today = toDateStr(new Date());
    return today >= rangeFrom && today <= rangeTo;
  }, [rangeFrom, rangeTo]);

  // Active events overlapping the range + birthdays/anniversaries in the range,
  // both computed on the server.
  const { data: rangeEvents, loading, refetch } = useFetchList<ChurchEvent>(
    (p, o) => eventList.range(p, o), { params: { from: rangeFrom, to: rangeTo } }
  );
  const { data: celebrations } = useFetchList<any>(
    (p, o) => celebrationList.page(p, o), { params: { from: rangeFrom, to: rangeTo, size: 100000 } }
  );

  const viewedYear = currentDate.getFullYear();

  const feed = useMemo(() => {
    // rangeEvents are already active (the backend excludes voided rows).
    const real = categoryFilter ? rangeEvents.filter((e) => e.category === categoryFilter) : rangeEvents;

    const life = [];
    // A category filter is about scheduled events, so it hides the auto records.
    if (!categoryFilter) {
      const fromMd = rangeFrom.slice(5);
      getList<any>(celebrations).forEach((row) => {
        const kind = row.type === 'BIRTHDAY' ? 'birthday' : 'anniversary';
        if (kind === 'birthday' && hideBirthdays) return;
        if (kind === 'anniversary' && hideAnniversaries) return;
        life.push(toLifeEvent(row, fromMd, rangeFrom, rangeTo));
      });
    }
    return [...real, ...life];
  }, [rangeEvents, celebrations, categoryFilter, hideBirthdays, hideAnniversaries, rangeFrom, rangeTo]);

  // Real events span their whole date range; life events sit on one day.
  const eventsByDate = useMemo(() => {
    const map = new Map<string, ChurchEvent[]>();
    const addTo = (dateStr: string, ev) => {
      const list = map.get(dateStr) || [];
      list.push(ev);
      map.set(dateStr, list);
    };
    feed.forEach((ev) => {
      if (!ev.eventDate) return;
      if (!ev.isLifeEvent && ev.endDate && ev.endDate > ev.eventDate) {
        const cur = new Date(`${ev.eventDate}T00:00:00`);
        const end = new Date(`${ev.endDate}T00:00:00`);
        for (let guard = 0; cur <= end && guard < 90; guard += 1) {
          addTo(toDateStr(cur), ev);
          cur.setDate(cur.getDate() + 1);
        }
      } else {
        addTo(ev.eventDate, ev);
      }
    });
    map.forEach((list) => list.sort(byTime));
    return map;
  }, [feed]);

  const eventsOn = (dateStr) => eventsByDate.get(dateStr) || [];

  /**
   * The period's events as one date-ordered list. The screen shows a grid,
   * which does not read as a schedule on paper, so the printed sheet is built
   * from the same feed sorted by date and time (CHM-060).
   */
  const agendaRows = useMemo(
    () => [...feed]
      .filter((e) => e.eventDate)
      .sort((a, b) => String(a.eventDate).localeCompare(String(b.eventDate))
        || String(a.time || '').localeCompare(String(b.time || '')))
      .map((e) => ({
        eventDate: formatDate(e.eventDate),
        time: e.time || '',
        eventName: e.eventName || e.title || e.name || '',
        category: e.category || '',
        venue: e.venue || '',
        organiser: e.organiser || '',
        eventStatus: e.eventStatus || ''
      })),
    [feed]
  );

  const step = (direction) => {
    const next = new Date(currentDate);
    if (viewMode === 'month' || viewMode === 'agenda') next.setMonth(currentDate.getMonth() + direction);
    else if (viewMode === 'week') next.setDate(currentDate.getDate() + direction * 7);
    else next.setDate(currentDate.getDate() + direction);
    setCurrentDate(next);
  };

  const headerLabel = useMemo(() => {
    if (viewMode === 'week') {
      const start = new Date(currentDate);
      start.setDate(start.getDate() - start.getDay());
      const end = new Date(start);
      end.setDate(start.getDate() + 6);
      return `Week of ${start.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} — ` +
             `${end.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}`;
    }
    if (viewMode === 'day') return formatFriendlyDate(toDateStr(currentDate));
    return currentDate.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
  }, [currentDate, viewMode]);

  const openQuickAdd = (dateStr?: string) => {
    setSelectedDate(null);
    setClash([]);
    setQuickAdd({ ...EMPTY_QUICK, eventDate: dateStr || toDateStr(currentDate) });
  };

  const closeQuickAdd = () => { setQuickAdd(null); setClash([]); };

  const saveQuickAdd = async () => {
    if (!quickAdd?.eventName.trim() || !quickAdd.eventDate) {
      notify('Event name and date are required.', 'error');
      return;
    }

    // Warn about anything already on that day before writing. The calendar
    // holds only its visible range, so the chosen date is asked of the server
    // rather than trusting whatever happens to be loaded — picking a date in
    // another month would otherwise always look free. Celebrations come from a
    // separate feed, so birthdays never raise a false clash.
    if (!clash.length) {
      setSaving(true);
      let already: ChurchEvent[] = [];
      try {
        already = getList<ChurchEvent>(
          await eventList.range({ from: quickAdd.eventDate, to: quickAdd.eventDate })
        );
      } catch {
        // A check that fails must not stop the user saving; fall through.
        already = [];
      } finally {
        setSaving(false);
      }
      if (already.length) { setClash(already); return; }
    }

    setSaving(true);
    try {
      await eventsApi.create({
        eventName: quickAdd.eventName.trim(),
        category: quickAdd.category,
        eventDate: quickAdd.eventDate,
        time: quickAdd.time,
        venue: quickAdd.venue,
        status: 'ACTIVE'
      } as Partial<ChurchEvent>);
      await refetch();
      notify('Event added to the calendar.', 'success');
      setQuickAdd(null);
      setClash([]);
    } catch (err) {
      notify((err as Error)?.message || 'Could not add the event.', 'error');
    } finally {
      setSaving(false);
    }
  };

  // Exports cover every active event (one call), not just the visible range.


  // --- Local styles ---
  const cal: Record<string, CSSProperties> = {
    body: { flexGrow: 1, overflow: 'auto', display: 'flex', flexDirection: 'column' },
    grid: { display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gridAutoRows: '130px', gap: '1px', backgroundColor: t.border, border: `1px solid ${t.border}`, flexGrow: 1 },
    headerCell: { backgroundColor: isDarkMode ? '#2A2A2A' : '#FAFAFA', color: t.textMuted, padding: '10px', textAlign: 'center', fontWeight: 'bold', fontSize: t.fontSize.md, textTransform: 'uppercase' },
    cell: { backgroundColor: isDarkMode ? '#242424' : '#FFFFFF', padding: '8px', display: 'flex', flexDirection: 'column', overflow: 'hidden' },
    emptyCell: { backgroundColor: isDarkMode ? '#1A1A1A' : '#F9F9F9' },
    todayCell: { backgroundColor: t.primarySoft },
    dateNumber: { fontSize: t.fontSize.md, fontWeight: 'bold', color: t.textMuted, marginBottom: '5px', textAlign: 'right' },
    todayNumber: { color: t.primaryText, fontSize: t.fontSize.lg },
    eventList: { display: 'flex', flexDirection: 'column', gap: '4px', overflow: 'hidden', flexGrow: 1 },
    pillTime: { opacity: 0.75, marginRight: '4px' },
    moreLabel: { fontSize: t.fontSize.xs, color: t.textFaint, fontWeight: 'bold', cursor: 'pointer', paddingLeft: '4px' },
    weekGrid: { display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: '1px', backgroundColor: t.border, border: `1px solid ${t.border}`, flexGrow: 1 },
    weekColumn: { backgroundColor: isDarkMode ? '#242424' : '#FFFFFF', display: 'flex', flexDirection: 'column' },
    weekHeader: { padding: '10px', textAlign: 'center', borderBottom: `1px solid ${t.border}`, backgroundColor: isDarkMode ? '#2A2A2A' : '#FAFAFA' },
    weekHeaderToday: { backgroundColor: t.primarySoft },
    weekDayName: { fontSize: t.fontSize.sm, fontWeight: 'bold', color: t.textMuted, textTransform: 'uppercase' },
    weekDayDate: { fontSize: t.fontSize.lg, fontWeight: 'bold', color: t.textMain },
    weekContent: { padding: '8px', display: 'flex', flexDirection: 'column', gap: '8px', overflowY: 'auto', flexGrow: 1 },
    noEvents: { color: t.textFaint, fontSize: t.fontSize.sm, textAlign: 'center', paddingTop: '20px', fontStyle: 'italic' },
    toggleGroup: { display: 'flex', borderRadius: t.radius.md, overflow: 'hidden', border: `1px solid ${t.primary}` },
    activeToggle: { backgroundColor: t.primary, color: '#FFF', border: 'none', padding: '8px 12px', cursor: 'pointer', fontWeight: 'bold', fontSize: t.fontSize.md },
    inactiveToggle: { backgroundColor: t.bgRaised, color: t.primaryText, border: 'none', padding: '8px 12px', cursor: 'pointer', fontWeight: 'bold', fontSize: t.fontSize.md },
    jumpSelect: { ...styles.input, width: 'auto', margin: 0, padding: '6px 8px' }
  };

  const pillStyle = (ev): CSSProperties => {
    const c = colorOf(ev, t.primary);
    const cancelled = kindOf(ev) === 'cancelled';
    return {
      backgroundColor: isDarkMode ? '#2E2E2E' : '#F4F0F5',
      borderLeft: `4px solid ${c}`,
      color: cancelled ? t.textFaint : t.textMain,
      textDecoration: cancelled ? 'line-through' : 'none',
      padding: '3px 6px', borderRadius: t.radius.sm, fontSize: t.fontSize.xs, fontWeight: 'bold',
      cursor: 'pointer', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis'
    };
  };

  const renderMonthView = () => {
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    const firstDay = new Date(year, month, 1).getDay();
    const today = new Date();
    const cells = [];

    for (let i = 0; i < firstDay; i += 1) {
      cells.push(<div key={`empty-${i}`} style={{ ...cal.cell, ...cal.emptyCell }} />);
    }

    for (let day = 1; day <= daysInMonth; day += 1) {
      const dateStr = toDateStr(new Date(year, month, day));
      const dayEvents = eventsOn(dateStr);
      const isToday = isSameDay(new Date(year, month, day), today);

      cells.push(
        <div
          key={day}
          className="calendar-day-cell"
          style={{ ...cal.cell, ...(isToday ? cal.todayCell : {}), cursor: 'pointer' }}
          onClick={() => (dayEvents.length ? setSelectedDate(dateStr) : openQuickAdd(dateStr))}
          title={dayEvents.length ? '' : 'Click to add an event'}
        >
          <div style={{ ...cal.dateNumber, ...(isToday ? cal.todayNumber : {}) }}>{day}</div>
          <div style={cal.eventList}>
            {dayEvents.slice(0, MAX_PILLS_PER_DAY).map((ev, i) => (
              <div
                key={`${ev.id}-${i}`}
                style={pillStyle(ev)}
                title={`${ev.time} - ${ev.eventName}`}
                onClick={(e) => { e.stopPropagation(); setSelectedEvent(ev); }}
              >
                <span style={cal.pillTime}>{ev.time}</span>{ev.eventName}
              </div>
            ))}
            {dayEvents.length > MAX_PILLS_PER_DAY && (
              <div style={cal.moreLabel} onClick={(e) => { e.stopPropagation(); setSelectedDate(dateStr); }}>
                + {dayEvents.length - MAX_PILLS_PER_DAY} more events
              </div>
            )}
          </div>
        </div>
      );
    }

    return (
      <div style={cal.grid}>
        {WEEKDAYS_SHORT.map((d) => <div key={d} style={cal.headerCell}>{d}</div>)}
        {cells}
      </div>
    );
  };

  const renderWeekView = () => {
    const startOfWeek = new Date(currentDate);
    startOfWeek.setDate(startOfWeek.getDate() - startOfWeek.getDay());
    const today = new Date();

    return (
      <div style={cal.weekGrid}>
        {WEEKDAYS_LONG.map((name, i) => {
          const day = new Date(startOfWeek);
          day.setDate(startOfWeek.getDate() + i);
          const dayEvents = eventsOn(toDateStr(day));
          const isToday = isSameDay(day, today);

          return (
            <div key={name} style={cal.weekColumn}>
              <div style={{ ...cal.weekHeader, ...(isToday ? cal.weekHeaderToday : {}) }}>
                <div style={cal.weekDayName}>{name}</div>
                <div style={cal.weekDayDate}>{day.getDate()} {day.toLocaleDateString('en-US', { month: 'short' })}</div>
              </div>
              <div style={cal.weekContent}>
                {dayEvents.length === 0 ? (
                  <div style={cal.noEvents}>No events</div>
                ) : (
                  dayEvents.map((ev, idx) => (
                    <div key={`${ev.id}-${idx}`} style={weekCardStyle(ev)} onClick={() => setSelectedEvent(ev)}>
                      <div style={{ fontSize: t.fontSize.xs, fontWeight: 'bold', color: colorOf(ev, t.primaryText) }}>{ev.time}</div>
                      <div style={{ fontSize: t.fontSize.base, fontWeight: 'bold', color: t.textMain, margin: '2px 0', textDecoration: kindOf(ev) === 'cancelled' ? 'line-through' : 'none' }}>{ev.eventName}</div>
                      <div style={{ fontSize: t.fontSize.xs, color: t.textFaint }}>📍 {ev.venue || 'No venue'}</div>
                    </div>
                  ))
                )}
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  const weekCardStyle = (ev): CSSProperties => ({
    backgroundColor: isDarkMode ? '#242424' : '#FCFAFD',
    borderLeft: `4px solid ${colorOf(ev, t.primary)}`,
    padding: '8px', borderRadius: t.radius.sm, cursor: 'pointer',
    opacity: kindOf(ev) === 'cancelled' ? 0.7 : 1
  });

  const renderDayView = () => {
    const dayEvents = eventsOn(toDateStr(currentDate));
    return (
      <div style={{ ...cal.weekColumn, maxWidth: '760px', margin: '0 auto', width: '100%', border: `1px solid ${t.border}` }}>
        <div style={{ ...cal.weekHeader, ...(isSameDay(currentDate, new Date()) ? cal.weekHeaderToday : {}) }}>
          <div style={cal.weekDayName}>{WEEKDAYS_LONG[currentDate.getDay()]}</div>
          <div style={cal.weekDayDate}>{formatFriendlyDate(toDateStr(currentDate))}</div>
        </div>
        <div style={{ ...cal.weekContent, minHeight: '300px' }}>
          {dayEvents.length === 0
            ? <div style={cal.noEvents}>No events. <span style={{ color: t.primaryText, cursor: 'pointer' }} onClick={() => openQuickAdd(toDateStr(currentDate))}>Add one</span>.</div>
            : dayEvents.map((ev, idx) => (
              <div key={`${ev.id}-${idx}`} style={weekCardStyle(ev)} onClick={() => setSelectedEvent(ev)}>
                <div style={{ fontSize: t.fontSize.xs, fontWeight: 'bold', color: colorOf(ev, t.primaryText) }}>{ev.time}</div>
                <div style={{ fontSize: t.fontSize.base, fontWeight: 'bold', color: t.textMain, textDecoration: kindOf(ev) === 'cancelled' ? 'line-through' : 'none' }}>{ev.eventName}</div>
                <div style={{ fontSize: t.fontSize.xs, color: t.textFaint }}>📍 {ev.venue || 'No venue'}</div>
              </div>
            ))}
        </div>
      </div>
    );
  };

  const renderAgendaView = () => {
    const monthStart = toDateStr(new Date(currentDate.getFullYear(), currentDate.getMonth(), 1));
    const upcoming = [...feed]
      .filter((ev) => ev.eventDate && ev.eventDate >= monthStart)
      .sort((a, b) => String(a.eventDate).localeCompare(String(b.eventDate)) || byTime(a, b));

    if (upcoming.length === 0) return <div style={cal.noEvents}>No upcoming events from {formatFriendlyDate(monthStart)}.</div>;

    let lastDate = '';
    return (
      <div style={{ maxWidth: '760px', margin: '0 auto', width: '100%' }}>
        {upcoming.map((ev, idx) => {
          const header = ev.eventDate !== lastDate;
          lastDate = ev.eventDate;
          return (
            <div key={`${ev.id}-${idx}`}>
              {header && (
                <div style={{ fontWeight: 'bold', color: t.textMuted, fontSize: t.fontSize.sm, textTransform: 'uppercase', margin: '16px 0 8px', borderBottom: `1px solid ${t.border}`, paddingBottom: '4px' }}>
                  {formatFriendlyDate(ev.eventDate)}
                </div>
              )}
              <div style={{ ...weekCardStyle(ev), marginBottom: '6px' }} onClick={() => setSelectedEvent(ev)}>
                <div style={{ display: 'flex', justifyContent: 'space-between', gap: '10px', flexWrap: 'wrap' }}>
                  <span style={{ fontWeight: 'bold', color: t.textMain, textDecoration: kindOf(ev) === 'cancelled' ? 'line-through' : 'none' }}>
                    {ev.eventName} {ev.category && <Badge tone="neutral">{ev.category}</Badge>}
                  </span>
                  <span style={{ color: t.textFaint, fontSize: t.fontSize.sm }}>{ev.time} · {ev.venue || 'No venue'}</span>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  const legendItem = (color: string, label: string) => (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: '5px', fontSize: t.fontSize.sm, color: t.textMuted }}>
      <span style={{ width: '12px', height: '12px', borderRadius: '3px', backgroundColor: color, display: 'inline-block' }} />{label}
    </span>
  );

  const eventsForSelectedDate = selectedDate ? eventsOn(selectedDate) : [];
  const years = Array.from({ length: 9 }, (_, i) => new Date().getFullYear() - 3 + i);
  if (!years.includes(viewedYear)) years.push(viewedYear);

  return (
    <div>
      <style>{`
        .calendar-day-cell { transition: background-color 0.2s ease; }
        .calendar-day-cell:hover { background-color: ${isDarkMode ? '#2C2C2C' : '#F4F0F5'} !important; }
        .day-table-row { transition: background-color 0.1s; cursor: pointer; }
        .day-table-row:hover { background-color: ${isDarkMode ? '#333' : '#F3E5F5'}; }
      `}</style>

      <Notification notification={notification} onClose={dismiss} />

      <div style={styles.headerRow}>
        <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
          <Button variant="tab" style={{ marginLeft: 0 }} onClick={() => step(-1)}>← Prev</Button>
          <Button variant="tab" active={viewingToday} style={{ marginLeft: 0 }} onClick={() => setCurrentDate(new Date())}
            title={viewingToday ? 'This view includes today' : 'Jump to today'}>
            Today
          </Button>
          <Button variant="tab" style={{ marginLeft: 0 }} onClick={() => step(1)}>Next →</Button>

          {/* Jump straight to a month / year */}
          <select style={cal.jumpSelect} value={currentDate.getMonth()}
            onChange={(e) => setCurrentDate(new Date(viewedYear, +e.target.value, 1))}>
            {MONTHS.map((m, i) => <option key={m} value={i}>{m}</option>)}
          </select>
          <select style={cal.jumpSelect} value={viewedYear}
            onChange={(e) => setCurrentDate(new Date(+e.target.value, currentDate.getMonth(), 1))}>
            {years.sort((a, b) => a - b).map((y) => <option key={y} value={y}>{y}</option>)}
          </select>
        </div>

        <h2 style={{ ...styles.headerTitle, fontSize: '22px', textAlign: 'center', flexGrow: 1 }}>{headerLabel}</h2>

        <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
          <div style={cal.toggleGroup}>
            {['month', 'week', 'day', 'agenda'].map((mode) => (
              <button key={mode} style={viewMode === mode ? cal.activeToggle : cal.inactiveToggle} onClick={() => setViewMode(mode)}>
                {mode.charAt(0).toUpperCase() + mode.slice(1)}
              </button>
            ))}
          </div>
          <PrintButton
            module="Church Calendar"
            documentType="CALENDAR_AGENDA"
            size="sm"
            title="Calendar-Agenda"
            data={() => ({
              title: 'Calendar Agenda',
              reference: headerLabel,
              rowCount: agendaRows.length,
              rows: agendaRows
            })}
            onNotify={notify}
            isDarkMode={isDarkMode}
          />
          <Button write onClick={() => openQuickAdd()}>＋ Add Event</Button>
        </div>
      </div>

      {/* Filters + legend + export */}
      <div style={{ display: 'flex', gap: '16px', alignItems: 'center', flexWrap: 'wrap', padding: '10px 14px', margin: '0 0 10px', background: t.bgMain, border: t.border3d, borderRadius: t.radius.lg }}>
        <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
          <span style={{ fontSize: t.fontSize.base, color: t.textMuted, fontWeight: 'bold' }}>Category:</span>
          <Select value={categoryFilter} onChange={(e) => setCategoryFilter(e.target.value)}
            options={CATEGORIES} placeholder="All" containerStyle={{ margin: 0 }} style={{ margin: 0, width: 'auto' }} isDarkMode={isDarkMode} />
        </div>
        <label style={{ display: 'flex', alignItems: 'center', gap: '5px', fontSize: t.fontSize.sm, color: t.textMuted, cursor: 'pointer' }}>
          <input type="checkbox" checked={!hideBirthdays} onChange={(e) => setHideBirthdays(!e.target.checked)} /> 🎂 Birthdays
        </label>
        <label style={{ display: 'flex', alignItems: 'center', gap: '5px', fontSize: t.fontSize.sm, color: t.textMuted, cursor: 'pointer' }}>
          <input type="checkbox" checked={!hideAnniversaries} onChange={(e) => setHideAnniversaries(!e.target.checked)} /> 💍 Anniversaries
        </label>

        <span style={{ width: '1px', height: '20px', background: t.border }} />
        {legendItem(t.primary, 'Event')}
        {legendItem(BIRTHDAY_COLOR, 'Birthday')}
        {legendItem(ANNIVERSARY_COLOR, 'Anniversary')}
        {legendItem('#9CA3AF', 'Cancelled')}

      </div>

      {/* The interactive grid is for the screen; on paper it is replaced by the
          readable events list below. */}
      <div style={cal.body}>
        {loading
          ? <div style={{ ...cal.noEvents, paddingTop: '60px' }}>Loading calendar…</div>
          : viewMode === 'month' ? renderMonthView()
          : viewMode === 'week' ? renderWeekView()
          : viewMode === 'day' ? renderDayView()
          : renderAgendaView()}
      </div>


      {/* Daily schedule */}
      {selectedDate && (
        <Drawer
          title={formatFriendlyDate(selectedDate)}
          subtitle="DAILY SCHEDULE"
          width="700px"
          onClose={() => setSelectedDate(null)}
          footer={<Button write fullWidth onClick={() => openQuickAdd(selectedDate)}>＋ Add event on this day</Button>}
        >
          <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left' }}>
            <thead>
              <tr>
                <th style={styles.th}>S.No</th>
                <th style={styles.th}>Time</th>
                <th style={styles.th}>Event Name</th>
                <th style={styles.th}>Venue</th>
              </tr>
            </thead>
            <tbody>
              {eventsForSelectedDate.map((ev, index) => (
                <tr key={`${ev.id}-${index}`} className="day-table-row" style={styles.tr}
                  onClick={() => { setSelectedDate(null); setSelectedEvent(ev); }}>
                  <td style={{ ...styles.td, fontWeight: 'bold', color: t.textFaint }}>{index + 1}</td>
                  <td style={{ ...styles.td, fontWeight: 'bold' }}>{ev.time}</td>
                  <td style={{ ...styles.td, textDecoration: kindOf(ev) === 'cancelled' ? 'line-through' : 'none' }}>{ev.eventName}</td>
                  <td style={styles.td}>{ev.venue || '-'}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <div style={{ textAlign: 'center', marginTop: '15px', fontSize: t.fontSize.sm, color: t.textFaint, fontStyle: 'italic' }}>
            Click any row to view full event details
          </div>
        </Drawer>
      )}

      {/* Event details */}
      {selectedEvent && (
        <Drawer
          title={selectedEvent.eventName}
          onClose={() => setSelectedEvent(null)}
          footer={selectedEvent.isLifeEvent ? (
            <div style={{ textAlign: 'center', color: t.textMuted, fontSize: t.fontSize.base, fontStyle: 'italic' }}>
              Auto record from the member register.
            </div>
          ) : (
            // Opens the event in Event Management with its edit form ready (CHM-059);
            // the row is handed over, so nothing has to be looked up again there.
            <Button write fullWidth onClick={() => navigate('/events', { state: { editEvent: selectedEvent } })}>
              ✏️ Edit this event
            </Button>
          )}
        >
          <div style={{ marginBottom: '20px', display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
            <Badge>ID: {selectedEvent.eventId}</Badge>
            {selectedEvent.category && <Badge tone="neutral">{selectedEvent.category}</Badge>}
            {selectedEvent.eventStatus === 'Cancelled' && <Badge tone="error">Cancelled</Badge>}
          </div>

          {selectedEvent.isLifeEvent ? (
            <>
              <Drawer.Section title="Details" />
              <Drawer.Row label="Member" value={selectedEvent.memberName} />
              <Drawer.Row label="Date" value={formatFriendlyDate(selectedEvent.eventDate)} />
            </>
          ) : (
            <>
              <Drawer.Section title="Scheduling Details" />
              <Drawer.Row label="Date" value={formatFriendlyDate(selectedEvent.eventDate)} />
              <Drawer.Row label="Time" value={selectedEvent.time} />
              {selectedEvent.endDate && <Drawer.Row label="Ends" value={`${formatFriendlyDate(selectedEvent.endDate)}${selectedEvent.endTime ? ` ${selectedEvent.endTime}` : ''}`} />}
              <Drawer.Row label="Venue" value={selectedEvent.venue} />

              <Drawer.Section title="Management" />
              <Drawer.Row label="Organiser" value={selectedEvent.organiser} />
              {selectedEvent.organiserContact && <Drawer.Row label="Contact" value={selectedEvent.organiserContact} />}
              <Drawer.Row label="Budget" value={formatCurrency(selectedEvent.budget)} />
              {selectedEvent.description && (
                <>
                  <Drawer.Section title="Notes" />
                  <Drawer.Row value={selectedEvent.description} />
                </>
              )}
            </>
          )}
        </Drawer>
      )}

      {/* Quick add */}
      {quickAdd && (
        <Modal
          title="Add Event"
          width="560px"
          onClose={closeQuickAdd}
          isDarkMode={isDarkMode}
          footer={
            <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
              <Button variant="secondary" onClick={closeQuickAdd}>Cancel</Button>
              <Button write onClick={saveQuickAdd} disabled={saving}>
                {saving ? 'Checking…' : clash.length ? 'Add Anyway' : 'Add Event'}
              </Button>
            </div>
          }
        >
          {clash.length > 0 && (
            <div
              role="alert"
              style={{
                marginBottom: '14px', padding: '12px 14px',
                border: `1px solid ${t.warning}`, borderRadius: t.radius.md,
                backgroundColor: t.bgSubtle
              }}
            >
              <div style={{ fontWeight: 'bold', color: t.warning, marginBottom: '6px' }}>
                {clash.length === 1 ? 'An event is' : `${clash.length} events are`} already booked on this date
              </div>
              <ul style={{ margin: 0, paddingLeft: '18px', color: t.textMuted, fontSize: t.fontSize.sm }}>
                {clash.map((ev) => {
                  // Same date and same time is the real double-booking; same
                  // date alone is common and legitimate (morning and evening).
                  const sameTime = !!quickAdd.time && ev.time === quickAdd.time;
                  return (
                    <li key={ev.eventId || ev.id || ev.eventName} style={{ marginBottom: '2px' }}>
                      <strong>{ev.eventName}</strong>
                      {ev.time ? ` · ${/^\d{1,2}:\d{2}$/.test(ev.time) ? formatTime(ev.time) : ev.time}` : ''}
                      {ev.venue ? ` · ${ev.venue}` : ''}
                      {sameTime && (
                        <span style={{ color: t.danger, fontWeight: 'bold' }}> — same time</span>
                      )}
                    </li>
                  );
                })}
              </ul>
              <div style={{ fontSize: t.fontSize.sm, color: t.textFaint, marginTop: '8px' }}>
                Change the date or time above, or press Add Anyway to book it as well.
              </div>
            </div>
          )}
          <SearchableSelect
            label="Event Name" required
            value={quickAdd.eventName}
            onChange={(v) => setQuickAdd((q) => (q ? { ...q, eventName: v } : q))}
            storageKey="church_event_types"
          />
          <Select label="Category" value={quickAdd.category}
            onChange={(e) => setQuickAdd((q) => (q ? { ...q, category: e.target.value } : q))}
            options={CATEGORIES} placeholder="Select…" isDarkMode={isDarkMode} />
          <Input label="Date" type="date" required value={quickAdd.eventDate}
            onChange={(e) => { setClash([]); setQuickAdd((q) => (q ? { ...q, eventDate: e.target.value } : q)); }} />
          <Input label="Time" type="time" value={quickAdd.time}
            onChange={(e) => { setClash([]); setQuickAdd((q) => (q ? { ...q, time: e.target.value } : q)); }} />
          <Input label="Venue" value={quickAdd.venue}
            onChange={(e) => setQuickAdd((q) => (q ? { ...q, venue: e.target.value } : q))} />
          <div style={{ fontSize: t.fontSize.sm, color: t.textFaint, marginTop: '6px' }}>
            For budget, organiser and more, use the Event Management module.
          </div>
        </Modal>
      )}
    </div>
  );
};

export default ChurchCalendar;
